<template>
    <section class="address-editor">
        <header class="address-editor__header">
            <span>{{ label }}</span>
            <Button
                v-if="editing && modelValue !== null"
                label="Clear"
                severity="secondary"
                variant="text"
                size="small"
                @click="clear"
            />
            <Button
                v-else-if="editing && !available"
                label="Add"
                icon="pi pi-plus"
                severity="secondary"
                variant="text"
                size="small"
                @click="start"
            />
        </header>

        <p v-if="!editing && modelValue === null" class="address-editor__empty">
            None. Edit to add one.
        </p>

        <template v-else-if="!editing">
            <div class="address-editor__read">
                <span v-for="line in lines" :key="line">{{ line }}</span>
                <span v-if="lines.length === 0" class="address-editor__muted">Nothing filled in yet.</span>
            </div>
            <!-- `available` is left at its default. It answers why the manual
                 fields are on screen, and there are no fields here — so this row
                 reports what the address records and nothing else. `present` is
                 true because the branch above already turned away a null one. -->
            <AddressStatus :verified="modelValue?.verified ?? null" present />
        </template>

        <template v-else>
            <div v-if="available" class="address-editor__field address-editor__search">
                <small>Find an address</small>
                <AutoComplete
                    v-model="searchTerm"
                    :suggestions="suggestions"
                    option-label="label"
                    :loading="searching"
                    :min-length="MINIMUM_QUERY"
                    :delay="DEBOUNCE_MS"
                    :complete-on-focus="false"
                    append-to="self"
                    fluid
                    placeholder="Start typing an address…"
                    class="address-editor__control"
                    @complete="runSuggest"
                    @option-select="onPick"
                />
            </div>

            <div v-else-if="modelValue !== null" class="address-editor__fields">
                <div v-for="field in STACKED_FIELDS" :key="field" class="address-editor__field">
                    <small>{{ ADDRESS_LABELS[field] }}</small>
                    <InputText
                        :model-value="modelValue[field] ?? ''"
                        size="small"
                        class="address-editor__control"
                        @update:model-value="(value) => write(field, value)"
                    />
                </div>

                <div class="address-editor__locality">
                    <div v-for="field in LOCALITY_FIELDS" :key="field" class="address-editor__field">
                        <small>{{ ADDRESS_LABELS[field] }}</small>
                        <InputText
                            :model-value="modelValue[field] ?? ''"
                            size="small"
                            class="address-editor__control"
                            @update:model-value="(value) => write(field, value)"
                        />
                    </div>
                </div>

                <div class="address-editor__field">
                    <small>{{ ADDRESS_LABELS.country }}</small>
                    <Select
                        :model-value="modelValue.country ?? null"
                        :options="COUNTRIES"
                        option-label="name"
                        option-value="code"
                        placeholder="Choose a country"
                        size="small"
                        class="address-editor__control"
                        @update:model-value="(value) => write('country', value)"
                    />
                </div>
            </div>

            <p v-else class="address-editor__empty">None. Add one to type it in.</p>

            <AddressStatus
                :available="available"
                :verified="modelValue?.verified ?? null"
                :present="modelValue !== null"
            />
        </template>
    </section>
</template>

<script setup>
import { administrationApiSdk } from '@hds/administration-client-sdk'
import AutoComplete from 'primevue/autocomplete'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import { computed, ref, watch } from 'vue'

import AddressStatus from '@/components/AddressStatus.vue'
import {
    ADDRESS_LABELS,
    COUNTRIES,
    DEFAULT_COUNTRY,
    addressLines
} from '@/lib/addressFields.js'

// One postal address, in one of two forms.
//
// **Which form depends on whether an address can be looked up at all.** With the
// address service reachable, the only control is the lookup box: an address is
// set by picking a suggestion, so what reaches storage has been resolved by the
// authoritative data and is marked `verified: true`. With it unreachable — no key,
// or a request that failed — the six fields are drawn instead and what is saved
// is marked `verified: false`, so the record itself admits it was never checked.
//
// One form at a time, and no question about which control is authoritative.
//
// It serves both addresses this app edits — an Auction House's `location`, which
// is geocoded, and a Company's billing address, which is not — because they are
// the same fields either way. The caller says which by passing `geocode`.
const props = defineProps({
    // The address, or null when there is none. Null is a value here: it is what
    // a PATCH sends to clear the property, and it is distinct from an absent key.
    modelValue: { type: Object, default: null },
    label: { type: String, required: true },
    editing: { type: Boolean, default: false },
    // Whether a point is kept with the address. A Company's billing address
    // declares no `geo`, so a point written there is a refused save.
    geocode: { type: Boolean, default: false }
})
const emit = defineEmits(['update:model-value'])

// The street line and the unit each take a row; the three short ones share one.
const STACKED_FIELDS = ['addressLine1', 'addressLine2']
const LOCALITY_FIELDS = ['city', 'region', 'postalCode']

// Both are handed to the control rather than enforced here, because the control
// already does both — `minLength` and `delay` are its own props. A timer and a
// length check in this file would be a second copy of a mechanism one layer
// down, and the one that drifted would be this one.
//
// Three characters, because Amazon Location bills per request and two match most
// of a country. Three hundred milliseconds is the control's own default, named
// here so the number is visible beside the reason for it.
const MINIMUM_QUERY = 3
const DEBOUNCE_MS = 300

// What is in the search box. Deliberately not part of the address: it is a
// question, and it is replaced by the answer as soon as one arrives.
//
// The answer is the address itself, written out on one line. This form has no
// other control, so a box left holding the search term — or emptied back to its
// placeholder — would be a form that does not show what it is editing.
const searchTerm = ref('')
const suggestions = ref([])
const searching = ref(false)

// Whether this deployment can look an address up. Asked before anything is
// typed, because the whole form depends on the answer — a form that decided
// after the first keystroke would change shape under somebody's hands.
//
// It starts false so the manual fields are what appears while the answer is
// still coming: a form somebody can type into is the safe thing to be wrong
// about, and the lookup box would be a control that does nothing.
const available = ref(false)

// Which request the box is waiting for. Two answers to two prefixes arrive in
// whatever order the network gives them, and the slower one must not win. This
// the control does not do, so it stays.
let latest = 0

const lines = computed(() => addressLines(props.modelValue))

watch(() => props.editing, async function (editing) {
    if (editing === false) {
        forget()
        return
    }
    showAddressInBox()
    await askWhetherAvailable()
}, { immediate: true })

/**
 * Whether an address can be looked up, from the deployment rather than a guess.
 *
 * A request that never arrives is treated the same as a deployment with no key:
 * either way nobody can look an address up, so either way the manual fields are
 * what the form owes them.
 */
async function askWhetherAvailable() {
    try {
        available.value = (await administrationApiSdk.places.status()).configured
    } catch {
        available.value = false
    }
}

function forget() {
    suggestions.value = []
    showAddressInBox()
}

/** The address as one line, which is what the box reads when nobody is typing. */
function showAddressInBox() {
    searchTerm.value = lines.value.join(', ')
}

/**
 * Only the manual form offers this.
 *
 * The lookup form has no Add button on purpose: an address it created would
 * satisfy no required field and could only be finished by a pick, so pressing
 * Add and then Save would earn a refusal with nothing on screen to fix. There,
 * the first pick is what creates the address.
 */
function start() {
    forget()
    emit('update:model-value', { country: DEFAULT_COUNTRY, verified: false })
}

function clear() {
    forget()
    emit('update:model-value', null)
}

/** Typed by hand, so the address says so — and loses the point that went with it. */
function write(field, value) {
    const address = { ...props.modelValue, [field]: value, verified: false }
    // The point named the building the old address stood at. Once a field moves
    // by hand it names somewhere else, so it is cleared the way this platform
    // clears anything: with a null, rather than by dropping the key.
    //
    // Only where there is one to clear. A Company's billing address declares no
    // `geo` behind a closed shape, and a key it does not know is a refused save.
    if (address.geo !== undefined) {
        address.geo = null
    }
    emit('update:model-value', address)
}

async function runSuggest(event) {
    const typed = event.query.trim()
    latest = latest + 1
    const ticket = latest
    searching.value = true
    let answer = null
    try {
        answer = await administrationApiSdk.places.autocomplete(
            typed,
            props.modelValue?.country ?? DEFAULT_COUNTRY
        )
    } catch {
        // Nobody can look an address up any more, so the form owes them the one
        // they can type into.
        available.value = false
        return
    }
    if (ticket !== latest) {
        return
    }
    available.value = answer.configured
    suggestions.value = answer.suggestions
    searching.value = false
}

/**
 * A picked suggestion, resolved to the address that gets stored.
 *
 * This is the only way an address is set while the service is reachable, and the
 * point of that: nothing here passed through a keyboard, so there is nothing to
 * mistype and nothing to check afterwards. It is `verified` by construction.
 *
 * The search box empties, because the question it held has been answered.
 */
async function onPick(event) {
    let answer = null
    try {
        answer = await administrationApiSdk.places.readPlace(event.value.placeId)
    } catch {
        available.value = false
        showAddressInBox()
        return
    }
    if (answer.address === undefined) {
        showAddressInBox()
        // The absence is the answer: the suggestion named something coarser than
        // a building, and there is nothing here anybody can store.
        return
    }
    const address = addressToEmit(answer)
    searchTerm.value = addressLines(address).join(', ')
    emit('update:model-value', address)
}

/**
 * What a resolved answer becomes in the draft.
 *
 * `addressLine2` is carried over rather than taken from the answer. The
 * authoritative data does not speak on unit designators, so a suite already
 * recorded would be dropped by a wholesale replacement — and this form has no
 * box to type it back into.
 *
 * The point is kept only where the document has somewhere to put one.
 */
function addressToEmit(answer) {
    const address = { ...answer.address, verified: true }
    if (props.modelValue?.addressLine2 !== undefined && props.modelValue.addressLine2 !== '') {
        address.addressLine2 = props.modelValue.addressLine2
    }
    if (props.geocode && answer.geo !== undefined) {
        address.geo = answer.geo
    }
    return address
}
</script>

<style scoped>
.address-editor {
    border-top: 1px solid var(--p-content-border-color);
    margin-top: 12px;
    padding-top: 12px;
}

.address-editor__header {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
}

.address-editor__header span {
    font-size: 13px;
    font-weight: 600;
}

.address-editor__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 4px 0 0;
}

/* An address reads as the lines somebody would write on an envelope. */
.address-editor__read {
    display: flex;
    flex-direction: column;
    font-size: 13px;
    line-height: 1.5;
    margin-top: 8px;
}

.address-editor__muted {
    color: var(--p-surface-400);
    font-size: 12px;
}

.address-editor__search {
    margin-top: 10px;
}

.address-editor__fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
}

.address-editor__field {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.address-editor__field small {
    color: var(--p-surface-400);
    font-size: 11px;
}

/* City, state and postal code have no business taking a row each in a rail
   this narrow. The state column is the narrowest: it holds two letters. */
.address-editor__locality {
    display: grid;
    gap: 8px;
    grid-template-columns: 1.6fr 1fr 1.2fr;
}

.address-editor__control {
    width: 100%;
}
</style>
